import { useMemo, useState } from "react";
import type { SocialMensajesPanorama } from "../../../types";
import { useIsMobile } from "../mobile/MobileChrome";
import { DIAS_CORTO, MESES, MESES_LARGO, etiquetaTramo, matrizHoras, picoHoras, serieMensual } from "./mensajesUtil";

const n = (v: number) => v.toLocaleString("es-CL");

interface Tip {
  x: number;
  y: number;
  titulo: string;
  lineas: string[];
}

function Tooltip({ tip }: { tip: Tip | null }) {
  if (!tip) return null;
  return (
    <div className="msj-tip" role="presentation" style={{ left: Math.min(tip.x + 12, window.innerWidth - 250), top: tip.y + 14 }}>
      <b>{tip.titulo}</b>
      {tip.lineas.map((l) => (
        <span key={l}>{l}</span>
      ))}
    </div>
  );
}

function GraficoMensual({ datos, meses, onTip }: { datos: SocialMensajesPanorama["mensual"]; meses: number; onTip: (t: Tip | null) => void }) {
  const serie = useMemo(() => serieMensual(datos, meses), [datos, meses]);
  const W = 640;
  const H = 210;
  const L = 28;
  const R = 6;
  const T = 10;
  const B = 28;
  const iw = W - L - R;
  const ih = H - T - B;
  const tope = Math.max(4, Math.ceil(Math.max(...serie.map((s) => s.conversaciones)) / 4) * 4);
  const bw = iw / serie.length;
  const ancho = bw * 0.62;
  const marcas = [0, 1, 2, 3, 4].map((i) => (tope / 4) * i);
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Conversaciones nuevas por mes, con y sin respuesta">
        {marcas.map((v) => {
          const y = T + ih - (v / tope) * ih;
          return (
            <g key={v}>
              <line className="grid" x1={L} x2={W - R} y1={y} y2={y} />
              <text x={L - 6} y={y + 4} textAnchor="end">{v}</text>
            </g>
          );
        })}
        {serie.map((s, i) => {
          const x = L + i * bw + (bw - ancho) / 2;
          const con = s.conversaciones - s.sinRespuesta;
          const hC = (con / tope) * ih;
          const hS = (s.sinRespuesta / tope) * ih;
          const base = T + ih;
          const [anio, mes] = s.mes.split("-");
          return (
            <g key={s.mes}>
              {con > 0 && <rect x={x} y={base - hC} width={ancho} height={hC} rx={2} fill="var(--m-s-blue)" />}
              {s.sinRespuesta > 0 && <rect x={x} y={base - hC - hS - (con > 0 ? 2 : 0)} width={ancho} height={hS} rx={2} fill="var(--m-s-orange)" />}
              <rect
                x={L + i * bw}
                y={T}
                width={bw}
                height={ih}
                fill="transparent"
                onMouseMove={(e) =>
                  onTip({
                    x: e.clientX,
                    y: e.clientY,
                    titulo: `${MESES_LARGO[+mes - 1]} ${anio}`,
                    lineas: [`${n(s.conversaciones)} conversaciones`, `${n(con)} con respuesta · ${n(s.sinRespuesta)} sin respuesta`],
                  })
                }
                onMouseLeave={() => onTip(null)}
              />
              {i % 3 === 0 && (
                <text x={x + ancho / 2} y={H - 8} textAnchor="middle">
                  {MESES[+mes - 1]} {anio.slice(2)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <details>
        <summary>Ver como tabla</summary>
        <div className="msj-tbl">
          <table>
            <thead>
              <tr><th>Mes</th><th>Conversaciones</th><th>Sin respuesta</th></tr>
            </thead>
            <tbody>
              {serie.map((s) => (
                <tr key={s.mes}><td>{s.mes}</td><td className="num">{n(s.conversaciones)}</td><td className="num">{n(s.sinRespuesta)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}

function Antiguedad({ datos, umbrales, onTip }: { datos: SocialMensajesPanorama["antiguedad"]; umbrales: SocialMensajesPanorama["umbrales"]; onTip: (t: Tip | null) => void }) {
  const maximo = Math.max(1, ...datos.map((d) => d.lead + d.otra + d.info));
  const total = datos.reduce((a, d) => a + d.lead + d.otra + d.info, 0);
  const desdeDia = (i: number) => (i === 0 ? 0 : umbrales.tramos[i - 1]);
  const viejas = datos.filter((_, i) => desdeDia(i) >= umbrales.vencida).reduce((a, d) => a + d.lead + d.otra + d.info, 0);
  const segmento = (cant: number, color: string, nombre: string) =>
    cant > 0 ? (
      <div
        className="msj-seg"
        style={{ width: `${(cant / maximo) * 100}%`, background: `var(${color})` }}
        onMouseMove={(e) => onTip({ x: e.clientX, y: e.clientY, titulo: nombre, lineas: [`${n(cant)} conversaciones`] })}
        onMouseLeave={() => onTip(null)}
      />
    ) : null;
  return (
    <>
      <div className="msj-bars">
        {datos.map((d, i) => (
          <div key={d.tramo} className="msj-brow">
            <span>{etiquetaTramo(i, umbrales.tramos)}</span>
            <div className="msj-track">
              {segmento(d.lead, "--m-s-orange", "Posibles leads")}
              {segmento(d.otra, "--m-s-blue", "Otras")}
              {segmento(d.info, "--m-s-green", "Informativas")}
            </div>
            <span className="val num">{n(d.lead + d.otra + d.info)}</span>
          </div>
        ))}
      </div>
      <p className="msj-note">
        {total > 0
          ? `${n(viejas)} de ${n(total)} conversaciones sin respuesta (${Math.round((viejas / total) * 100)}%) tienen más de ${umbrales.vencida} días.`
          : "No hay conversaciones sin respuesta."}
      </p>
      <details>
        <summary>Ver como tabla</summary>
        <div className="msj-tbl">
          <table>
            <thead>
              <tr><th>Antigüedad</th><th>Leads</th><th>Otras</th><th>Informativas</th></tr>
            </thead>
            <tbody>
              {datos.map((d, i) => (
                <tr key={d.tramo}>
                  <td>{etiquetaTramo(i, umbrales.tramos)}</td>
                  <td className="num">{n(d.lead)}</td><td className="num">{n(d.otra)}</td><td className="num">{n(d.info)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}

function Calor({ horas, meses, onTip }: { horas: SocialMensajesPanorama["horas"]; meses: number; onTip: (t: Tip | null) => void }) {
  const matriz = useMemo(() => matrizHoras(horas), [horas]);
  const maximo = Math.max(1, ...matriz.flat());
  const pico = picoHoras(matriz);
  const movil = useIsMobile();
  const porDia = matriz.map((fila) => fila.reduce((a, b) => a + b, 0));
  const maxDia = Math.max(1, ...porDia);
  const nota = (
    <p className="msj-note">
      {pico ? `El pico de mensajes está entre las ${pico.desde} y las ${pico.hasta} h. ` : "Sin mensajes en el período. "}
      Horas de Santiago, últimos {meses} meses.
    </p>
  );
  if (movil)
    return (
      <>
        <div className="msj-bars msj-dias" role="img" aria-label="Mensajes de clientes por día de la semana">
          {porDia.map((total, d) => (
            <div key={DIAS_CORTO[d]} className="msj-brow msj-brow-dia">
              <span>{DIAS_CORTO[d]}</span>
              <div className="msj-track">
                <div className="msj-seg" style={{ width: `${(total / maxDia) * 100}%`, background: "var(--m-s-blue)" }} />
              </div>
              <span className="val num">{n(total)}</span>
            </div>
          ))}
        </div>
        {nota}
      </>
    );
  return (
    <>
      <div className="msj-heat" role="img" aria-label="Mensajes de clientes por día de la semana y hora">
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} className="hl num">{h % 3 === 0 ? h : ""}</span>
        ))}
        {matriz.map((fila, d) => (
          <div key={DIAS_CORTO[d]} className="msj-heat-row">
            <span className="lab">{DIAS_CORTO[d]}</span>
            {fila.map((v, h) => (
              <div
                key={h}
                className="cell"
                style={{ background: v ? `color-mix(in srgb, var(--m-blue) ${Math.max(14, Math.round((v / maximo) * 100))}%, var(--m-surface-2))` : undefined }}
                onMouseMove={(e) =>
                  onTip({ x: e.clientX, y: e.clientY, titulo: `${DIAS_CORTO[d]} ${String(h).padStart(2, "0")}:00`, lineas: [`${n(v)} mensajes`] })
                }
                onMouseLeave={() => onTip(null)}
              />
            ))}
          </div>
        ))}
      </div>
      {nota}
    </>
  );
}

function Respuesta({ datos, meses, autorrespuesta, onTip }: { datos: SocialMensajesPanorama["respuesta"]; meses: number; autorrespuesta: SocialMensajesPanorama["autorrespuesta"]; onTip: (t: Tip | null) => void }) {
  const filas: Array<[string, number, string]> = [
    ["Hasta 1 hora", datos.h1, "--m-s-blue"],
    ["Entre 1 y 24 horas", datos.h24, "--m-s-blue"],
    ["Más de 24 horas", datos.mas24, "--m-s-blue"],
    ["Sin respuesta", datos.sin_respuesta, "--m-s-orange"],
  ];
  const total = Math.max(1, datos.conversaciones);
  return (
    <>
      <div className="msj-bars">
        {filas.map(([nombre, cant, color]) => (
          <div key={nombre} className="msj-brow">
            <span>{nombre}</span>
            <div className="msj-track">
              {cant > 0 && (
                <div
                  className="msj-seg"
                  style={{ width: `${(cant / total) * 100}%`, background: `var(${color})` }}
                  onMouseMove={(e) => onTip({ x: e.clientX, y: e.clientY, titulo: nombre, lineas: [`${n(cant)} conversaciones`] })}
                  onMouseLeave={() => onTip(null)}
                />
              )}
            </div>
            <span className="val num">{n(cant)}</span>
          </div>
        ))}
      </div>
      <p className="msj-note">
        {n(datos.conversaciones)} conversaciones con mensajes de clientes en {meses} meses.
        {datos.mediana_min !== null && ` La mediana es de ${n(datos.mediana_min)} minutos.`}
        {autorrespuesta &&
          ` Un mismo mensaje de la página salió ${n(autorrespuesta.veces)} veces («${autorrespuesta.texto.trim()}…») y hoy cuenta como respuesta, así que la rapidez real de una persona no se mide.`}
      </p>
    </>
  );
}

export function MensajesGraficos({ panorama }: { panorama: SocialMensajesPanorama }) {
  const [tip, setTip] = useState<Tip | null>(null);
  return (
    <section aria-label="Contexto">
      <div className="msj-sec-h">
        <h2>Cómo llegan y cómo se atienden</h2>
        <span>Últimos {panorama.meses} meses</span>
      </div>
      <div className="msj-grid2">
        <div className="msj-card">
          <h3>Conversaciones nuevas por mes</h3>
          <div className="msj-legend">
            <span><i style={{ background: "var(--m-s-blue)" }} />Con respuesta</span>
            <span><i style={{ background: "var(--m-s-orange)" }} />Sin respuesta</span>
          </div>
          <GraficoMensual datos={panorama.mensual} meses={panorama.meses} onTip={setTip} />
        </div>
        <div className="msj-card">
          <h3>Antigüedad de lo que sigue sin respuesta</h3>
          <div className="msj-legend">
            <span><i style={{ background: "var(--m-s-orange)" }} />Posibles leads</span>
            <span><i style={{ background: "var(--m-s-blue)" }} />Otras</span>
            <span><i style={{ background: "var(--m-s-green)" }} />Informativas</span>
          </div>
          <Antiguedad datos={panorama.antiguedad} umbrales={panorama.umbrales} onTip={setTip} />
        </div>
        <div className="msj-card">
          <h3>Cuándo escriben los clientes</h3>
          <Calor horas={panorama.horas} meses={panorama.meses} onTip={setTip} />
        </div>
        <div className="msj-card">
          <h3>Tiempo hasta la primera respuesta</h3>
          <Respuesta datos={panorama.respuesta} meses={panorama.meses} autorrespuesta={panorama.autorrespuesta} onTip={setTip} />
        </div>
      </div>
      <Tooltip tip={tip} />
    </section>
  );
}
